'use client';

import React, { useEffect, useRef } from 'react';

const flowSteps = [
  {
    step: '01',
    label: 'Source Data Extraction',
    systems: ['S/4HANA GL', 'SD/MM Modules', 'HR Systems', 'CRM / External'],
    desc: 'Actuals, master data, and hierarchies extracted from source systems on defined schedules.',
    direction: 'right',
  },
  {
    step: '02',
    label: 'Data Foundation (Datasphere)',
    systems: ['Replication Layer', 'Transformation Layer', 'Business Layer', 'Data Quality'],
    desc: 'Data harmonised, validated, and structured to match SAC Planning dimension design.',
    direction: 'right',
  },
  {
    step: '03',
    label: 'SAC Planning Model Load',
    systems: ['Dimension Members', 'Hierarchy Sync', 'Actuals Load', 'Reference Data'],
    desc: 'Approved data loaded into SAC Planning models. Versioned as Actual, locked for planning reference.',
    direction: 'right',
  },
  {
    step: '04',
    label: 'Planning Input & Calculation',
    systems: ['Planner Input', 'Data Actions', 'Driver Formulas', 'Allocations'],
    desc: 'Planners enter assumptions. Data actions and script logic derive the plan from drivers.',
    direction: 'right',
  },
  {
    step: '05',
    label: 'Review & Approval',
    systems: ['Workflow Tasks', 'Variance Review', 'Approval Sign-off', 'Version Lock'],
    desc: 'Submitted plans reviewed against targets. Approved plans locked and published.',
    direction: 'right',
  },
  {
    step: '06',
    label: 'Reporting & Distribution',
    systems: ['Planning Stories', 'Executive Dashboards', 'Variance Reports', 'Export / Write-back'],
    desc: 'Approved plan data available for reporting, comparison to actuals, and operational use.',
    direction: 'end',
  },
];

const modelLayers = [
  { label: 'Planning Input Models', desc: 'Where planners enter data — typically one per planning domain.' },
  { label: 'Driver / Reference Models', desc: 'Store planning assumptions and drivers referenced by calculation logic.' },
  { label: 'Allocation Models', desc: 'Dedicated models for cost allocation calculations — separate from input models.' },
  { label: 'Reporting / Output Models', desc: 'Aggregated planning output optimised for reporting and dashboard performance.' },
];

export default function PlanningDataFlow() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal-on-scroll').forEach((el) => {
              (el as HTMLElement).classList.add('is-visible');
            });
          }
        });
      },
      { threshold: 0.03 }
    );
    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-20 px-6 border-b border-border">
      <div className="max-w-container mx-auto">
        {/* Planning Data Flow */}
        <div className="mb-16">
          <div className="mb-10 reveal-on-scroll">
            <div className="mono-label mb-3">End-to-End Flow</div>
            <h2
              className="font-heading font-semibold text-foreground"
              style={{ fontSize: 'clamp(1.5rem, 2.5vw, 2.25rem)', letterSpacing: '-0.025em' }}
            >
              SAC Planning Data Flow
            </h2>
            <p className="mt-2 text-sm text-muted font-body max-w-lg leading-relaxed">
              From source system extraction through to approved plan reporting — the six stages of a SAC Planning data flow.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {flowSteps.map((step, i) => (
              <div
                key={step.step}
                className={`reveal-on-scroll reveal-delay-${Math.min(i + 1, 6)} border border-border bg-white p-6 relative`}
                style={{ borderRadius: '4px' }}
              >
                {/* Step number */}
                <div className="flex items-center justify-between mb-4">
                  <span className="mono-label">{step.step}</span>
                  {step.direction !== 'end' && (
                    <span className="text-muted hidden lg:block" style={{ fontSize: '10px' }}>→</span>
                  )}
                </div>

                <h3 className="font-heading font-semibold text-foreground text-sm mb-2">
                  {step.label}
                </h3>
                <p className="text-xs text-muted font-body leading-relaxed mb-4">{step.desc}</p>

                <div className="flex flex-wrap gap-1.5">
                  {step.systems.map((sys) => (
                    <span key={sys} className="tag-pill" style={{ fontSize: '10px' }}>
                      {sys}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Planning Model Layers */}
        <div>
          <div className="mb-8 reveal-on-scroll">
            <div className="mono-label mb-3">Model Architecture</div>
            <h2
              className="font-heading font-semibold text-foreground"
              style={{ fontSize: 'clamp(1.5rem, 2.5vw, 2.25rem)', letterSpacing: '-0.025em' }}
            >
              Planning Model Layer Design
            </h2>
            <p className="mt-2 text-sm text-muted font-body max-w-lg leading-relaxed">
              A well-designed SAC Planning deployment separates models by function — avoiding the
              monolithic model anti-pattern that causes performance and maintenance problems.
            </p>
          </div>

          {/* Visual model layers */}
          <div className="reveal-on-scroll reveal-delay-2 border border-border bg-white p-8" style={{ borderRadius: '4px' }}>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-0 border border-border" style={{ borderRadius: '4px', overflow: 'hidden' }}>
              {modelLayers.map((layer, i) => (
                <div
                  key={layer.label}
                  className="p-5 border-b md:border-b-0 md:border-r border-border last:border-0"
                  style={{
                    background: i === 0
                      ? '#EEF4FF'
                      : i === 1
                      ? '#F0FFF4'
                      : i === 2
                      ? '#FFFBF0' :'#FFF0F8',
                  }}
                >
                  <div className="mono-label mb-2">{String(i + 1).padStart(2, '0')}</div>
                  <div className="font-heading font-semibold text-foreground text-xs mb-2 leading-snug">
                    {layer.label}
                  </div>
                  <p className="text-xs text-muted font-body leading-relaxed">{layer.desc}</p>
                </div>
              ))}
            </div>

            <div className="mt-6 pt-6 border-t border-border">
              <div className="mono-label mb-3">Key Design Principle</div>
              <p className="text-sm text-muted font-body leading-relaxed max-w-2xl">
                Separate planning input models from reporting models. Input models are optimised for
                write performance. Reporting models are optimised for read and aggregation performance.
                Allocation models are isolated to prevent calculation logic from polluting input models.
              </p>
            </div>
          </div>

          {/* S/4 → Datasphere → SAC diagram */}
          <div className="mt-8 reveal-on-scroll reveal-delay-3">
            <div className="mono-label mb-4">Recommended Integration Architecture</div>
            <div className="border border-border bg-secondary p-6" style={{ borderRadius: '4px' }}>
              <div className="flex flex-col md:flex-row items-stretch gap-0">
                {/* S/4HANA block */}
                <div className="flex-1 border border-border bg-white p-5 md:rounded-l" style={{ borderRadius: '4px 0 0 4px' }}>
                  <div className="mono-label mb-2">Source</div>
                  <div className="font-heading font-semibold text-foreground text-sm mb-3">S/4HANA</div>
                  <ul className="flex flex-col gap-1">
                    {['Finance (FI/CO)', 'Sales (SD)', 'Procurement (MM)', 'HR / Payroll'].map((s) => (
                      <li key={s} className="text-xs text-muted font-body flex gap-1.5">
                        <span className="text-border-strong">·</span>{s}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Arrow */}
                <div className="flex items-center justify-center px-2 py-4 md:py-0">
                  <div className="hidden md:flex items-center gap-1">
                    <div className="w-6 h-px bg-border-strong" />
                    <span className="text-muted" style={{ fontSize: '10px' }}>▶</span>
                  </div>
                  <div className="flex md:hidden items-center gap-1 flex-col">
                    <div className="w-px h-4 bg-border-strong" />
                    <span className="text-muted" style={{ fontSize: '10px' }}>▼</span>
                  </div>
                </div>

                {/* Datasphere block */}
                <div className="flex-1 border border-border bg-white p-5" style={{ borderTopRightRadius: '0', borderBottomRightRadius: '0' }}>
                  <div className="mono-label mb-2">Foundation</div>
                  <div className="font-heading font-semibold text-foreground text-sm mb-3">SAP Datasphere</div>
                  <ul className="flex flex-col gap-1">
                    {['Data Replication', 'Transformation', 'Harmonisation', 'Data Quality'].map((s) => (
                      <li key={s} className="text-xs text-muted font-body flex gap-1.5">
                        <span className="text-border-strong">·</span>{s}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Arrow */}
                <div className="flex items-center justify-center px-2 py-4 md:py-0">
                  <div className="hidden md:flex items-center gap-1">
                    <div className="w-6 h-px bg-border-strong" />
                    <span className="text-muted" style={{ fontSize: '10px' }}>▶</span>
                  </div>
                  <div className="flex md:hidden items-center gap-1 flex-col">
                    <div className="w-px h-4 bg-border-strong" />
                    <span className="text-muted" style={{ fontSize: '10px' }}>▼</span>
                  </div>
                </div>

                {/* SAC block */}
                <div className="flex-1 border border-border bg-white p-5 md:rounded-r" style={{ borderRadius: '0 4px 4px 0' }}>
                  <div className="mono-label mb-2">Planning</div>
                  <div className="font-heading font-semibold text-foreground text-sm mb-3">SAC Planning</div>
                  <ul className="flex flex-col gap-1">
                    {['Planning Models', 'Data Actions', 'Planning Stories', 'Governance'].map((s) => (
                      <li key={s} className="text-xs text-muted font-body flex gap-1.5">
                        <span className="text-border-strong">·</span>{s}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-border">
                <p className="text-xs text-muted font-body leading-relaxed">
                  <span className="font-medium text-foreground">Recommended pattern:</span> Route all source
                  system data through SAP Datasphere before loading to SAC Planning models. This provides
                  a clean separation between integration complexity and planning model design, and enables
                  data quality validation before data enters the planning environment.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}